import React, { createContext, type Dispatch, useEffect, useReducer, useState } from "react";
import { type AppState, initialAppState } from "./AppState";
import {
  AppAction,
  CalculationActionTypes,
  CalculatorEnv,
  combinedReducers,
  ConfigurationActionTypes,
  usesDefaultFrameContract,
} from "./sections";
import { fetchFrameContractData, getCalculatorEnvFromUrl, getSlugAndAccesscodeFromUrl } from "../api";
import { defaultFrameContractData20 as defaultFrameContract } from "@jobrad-gmbh/bike-leasing-calculator";
import { AnalyticsProvider } from "../tracking";
import { CookiesProvider } from "react-cookie";

const AppContext = createContext<AppState>(initialAppState);

const AppDispatchContext = createContext<Dispatch<AppAction>>(() => null);

const AppProvider: React.FC<{
  children: React.ReactNode;
  apiUrl?: string;
  ctaUrl?: string;
  calculatorEnv?: CalculatorEnv;
}> = ({ children, apiUrl, ctaUrl, calculatorEnv = "website" }) => {
  const [state, dispatch] = useReducer(combinedReducers, initialAppState);
  const { slug, accesscode } = getSlugAndAccesscodeFromUrl(window?.location?.href) || { slug: "", accesscode: "" };
  // Determine the calculator environment from the URL or use the provided one
  const resolvedCalculatorEnv = getCalculatorEnvFromUrl(window?.location?.href) ?? calculatorEnv;

  const [frameContractLoadingError, setFrameContractLoadingError] = useState<Error>();

  // Validate early - compute whether we need apiUrl
  const shouldUseDefaultFrameContract = usesDefaultFrameContract(resolvedCalculatorEnv, slug, accesscode);
  const needsApiUrl = !shouldUseDefaultFrameContract;

  // Throw synchronously during render if validation fails
  if (needsApiUrl && !apiUrl) {
    throw new Error("apiUrl is not defined");
  }

  if (frameContractLoadingError) {
    throw frameContractLoadingError;
  }

  useEffect(() => {
    if (shouldUseDefaultFrameContract) {
      dispatch({
        type: ConfigurationActionTypes.SET_FRAME_CONTRACT,
        payload: { frameContract: defaultFrameContract, calculatorEnv: resolvedCalculatorEnv, ctaUrl },
      });
      dispatch({
        type: CalculationActionTypes.INIT,
        payload: { frameContract: defaultFrameContract, calculatorEnv: resolvedCalculatorEnv },
      });
      dispatch({ type: CalculationActionTypes.CALCULATE, payload: {} });
    } else {
      // apiUrl is guaranteed to exist here due to early validation
      const contractUrl = `${apiUrl}/contracts/calculator/${slug}/${accesscode}`;

      fetchFrameContractData(contractUrl)
        .then((data) => {
          dispatch({
            type: ConfigurationActionTypes.SET_FRAME_CONTRACT,
            payload: { frameContract: data, calculatorEnv: resolvedCalculatorEnv, ctaUrl },
          });
          dispatch({
            type: CalculationActionTypes.INIT,
            payload: { frameContract: data, calculatorEnv: resolvedCalculatorEnv },
          });
          dispatch({ type: CalculationActionTypes.CALCULATE, payload: {} });
        })
        .catch((error) => {
          setFrameContractLoadingError(error);
        });
    }
  }, [resolvedCalculatorEnv, apiUrl, ctaUrl, slug, accesscode, shouldUseDefaultFrameContract]);

  return (
    <AppContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>
        <CookiesProvider>
          <AnalyticsProvider>{children}</AnalyticsProvider>
        </CookiesProvider>
      </AppDispatchContext.Provider>
    </AppContext.Provider>
  );
};

export { AppContext, AppDispatchContext, AppProvider };
